import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import * as artifactService from '../../services/artifactService';
import * as artifactTypeService from '../../services/artifactTypeService';
import { DynamicForm } from '../common/DynamicForm';

interface CreateArtifactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateArtifactModal: React.FC<CreateArtifactModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { darkMode } = useTheme();
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { data: artifactTypes, isLoading: isLoadingTypes } = useQuery(
    ['artifactTypes'],
    () => artifactTypeService.getArtifactTypes(),
    { enabled: isOpen }
  );
  
  // Reset selected type when modal opens
  useEffect(() => {
    if (isOpen && artifactTypes && artifactTypes.length > 0) {
      setSelectedTypeId(artifactTypes[0].id);
    }
  }, [isOpen, artifactTypes]);
  
  const selectedType = artifactTypes?.find(type => type.id === selectedTypeId);
  
  const handleSubmit = async (formValues: Record<string, any>) => {
    if (!selectedType) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Format the artifact data based on the type to match our existing viewers
      const artifactData: any = {
        type: selectedTypeId,
        name: formValues.name,
        phase: formValues.phase || undefined,
      };
      
      // Process tags if present
      if (formValues.tags) {
        artifactData.tags = formValues.tags.split(',').map((tag: string) => tag.trim());
      }
      
      // Format content based on artifact type to match our existing viewers
      switch (selectedTypeId) {
        case 'text':
          artifactData.content = formValues.content;
          break;
          
        case 'code':
          artifactData.content = formValues.content;
          artifactData.metadata = {
            language: formValues.language
          };
          break;
          
        case 'image':
          // Use image URL or uploaded image
          if (formValues.image_url) {
            artifactData.content = formValues.image_url;
          } else if (formValues.image_upload) {
            artifactData.content = formValues.image_upload;
          }
          
          // Add metadata
          artifactData.metadata = {
            alt_text: formValues.alt_text,
            description: formValues.description
          };
          break;
          
        case 'idea':
          // Format for IdeaArtifactViewer
          artifactData.content = {
            content: formValues.content,
            tags: formValues.tags ? formValues.tags.split(',').map((tag: string) => tag.trim()) : []
          };
          break;
          
        case 'critique':
          // Format for CritiqueArtifactViewer
          artifactData.content = {
            content: formValues.content,
            score: parseInt(formValues.score) || 0,
            strengths: formValues.strengths ? formValues.strengths.split('\n').filter(Boolean) : [],
            weaknesses: formValues.weaknesses ? formValues.weaknesses.split('\n').filter(Boolean) : [],
            suggestions: formValues.suggestions ? formValues.suggestions.split('\n').filter(Boolean) : []
          };
          break;
          
        case 'solution':
          // Format for SolutionArtifactViewer
          artifactData.content = {
            summary: formValues.summary,
            content: formValues.content,
            components: formValues.components ? formValues.components.split('\n')
              .filter(Boolean)
              .map((line: string) => ({
                title: line,
                content: ''
              })) : [],
            next_steps: formValues.next_steps ? formValues.next_steps.split('\n').filter(Boolean) : []
          };
          break;
          
        case 'document':
          // Format for DocumentArtifactViewer
          artifactData.content = {
            text: formValues.content,
            html: '' // We could add markdown-to-html conversion here
          };
          break;
          
        case 'diagram':
          // Format for DiagramArtifactViewer
          if (formValues.image_url) {
            artifactData.content = {
              url: formValues.image_url,
              description: formValues.description
            };
          } else if (formValues.image_upload) {
            artifactData.content = {
              image: formValues.image_upload,
              description: formValues.description
            };
          }
          break;
          
        default:
          // For other types, just include all form values
          artifactData.content = { ...formValues };
          delete artifactData.content.name; // Remove name from content
          delete artifactData.content.phase; // Remove phase from content
          delete artifactData.content.tags; // Remove tags from content
      }
      
      await artifactService.createArtifact(artifactData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create artifact');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className={`absolute inset-0 ${darkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div 
          className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}
        >
          <div className={`px-4 pt-5 pb-4 sm:p-6 sm:pb-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className="text-lg leading-6 font-medium mb-4">Create New Artifact</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                {error}
              </div>
            )}
            
            {isLoadingTypes ? (
              <div className="text-center py-4">Loading artifact types...</div>
            ) : artifactTypes && artifactTypes.length > 0 ? (
              <>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Artifact Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {artifactTypes.map(type => (
                      <button
                        key={type.id}
                        type="button"
                        className={`p-3 rounded-lg border text-left ${
                          selectedTypeId === type.id
                            ? darkMode 
                              ? 'bg-blue-900 border-blue-700 text-white' 
                              : 'bg-blue-50 border-blue-300 text-blue-700'
                            : darkMode 
                              ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                              : 'bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedTypeId(type.id)}
                      >
                        <div className="flex items-center">
                          <span className="text-2xl mr-2">{type.icon}</span>
                          <div>
                            <div className="font-medium">{type.name}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {type.description}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {selectedType && (
                  <DynamicForm
                    schema={selectedType.schema}
                    onSubmit={handleSubmit}
                    submitLabel="Create Artifact"
                    isSubmitting={isSubmitting}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-4">No artifact types available</div>
            )}
            
            <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className={`px-4 py-2 rounded-md ${
                  darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
